/**
 * Inpakken — lijst van webshop-orders die ingepakt moeten worden.
 *
 * Scan label is de hoofdactie (StickyActionBar); de scanner opent in een
 * WbModal 'volledig' (BarcodeScanner). Tik op een rij opent de order.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInpakkenStore, useHeader } from '../stores';
import { BarcodeScanner } from '../components/barcode/BarcodeScanner';
import { WbButton, WbChip, WbFlag, WbListRow, WbSectionKop, WbState, StickyActionBar } from '../components/wb';

export function InpakkenListPage() {
  const navigate = useNavigate();
  const { orders, isLoading, isRefreshing, error, fetchOrders, startPolling, stopPolling } =
    useInpakkenStore();

  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  useEffect(() => {
    fetchOrders();
    startPolling();
    return () => {
      stopPolling();
    };
  }, [fetchOrders, startPolling, stopPolling]);

  const handleScanDetected = useCallback(
    async (code: string) => {
      setScannerOpen(false);
      setScanError(null);
      setIsLookingUp(true);

      try {
        const order = await useInpakkenStore.getState().getOrderByTracking(code.trim());
        if (order) {
          navigate(`/inpakken/${order.orderId}`);
        } else {
          setScanError(`Geen order gevonden voor barcode ${code}`);
        }
      } finally {
        setIsLookingUp(false);
      }
    },
    [navigate],
  );

  useHeader('Webshop', `${orders.length}`);

  return (
    <div className="space-y-4 pb-[140px]">
      {scanError && (
        <WbFlag kind="opmerking" title="Niet gevonden">{scanError}. Tik hieronder op een order of scan opnieuw.</WbFlag>
      )}

      {isLoading && orders.length === 0 && <WbState variant="laden" titel="Orders laden…" />}
      {!isLoading && error && <WbState variant="fout" titel={error} onOpnieuw={() => fetchOrders()} />}
      {!isLoading && !error && orders.length === 0 && (
        <WbState variant="leeg" titel="Niets meer in te pakken." tekst="Alle webshop-orders zijn verwerkt." />
      )}

      {!isLoading && !error && orders.length > 0 && (
        <section className="space-y-1.5">
          <WbSectionKop
            label="Te doen"
            teller={orders.length}
            actie={
              <WbButton variant="link" onClick={() => fetchOrders(true)} disabled={isRefreshing}>
                {isRefreshing ? 'Verversen…' : 'Ververs'}
              </WbButton>
            }
          />
          {orders.map((order) => {
            const itemCount = order.items?.length ?? 0;
            const plaats = [order.postalCode, order.city].filter(Boolean).join(' ');
            return (
              <WbListRow
                key={order.orderId}
                onClick={() => navigate(`/inpakken/${order.orderId}`)}
                hoofd={order.orderName}
                chips={<WbChip kind="neutraal">{itemCount} {itemCount === 1 ? 'item' : 'items'}</WbChip>}
                sub={
                  <>
                    <span className="font-body text-base text-wb-inkt">{order.customerName || 'Onbekende klant'}</span>
                    {plaats && <span> · {plaats}</span>}
                    {order.trackingNumber && <span className="block text-xs">{order.trackingNumber}</span>}
                  </>
                }
              />
            );
          })}
        </section>
      )}

      <StickyActionBar>
        <WbButton
          size="lg"
          onClick={() => {
            setScanError(null);
            setScannerOpen(true);
          }}
          isLoading={isLookingUp}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 7V5a2 2 0 012-2h2M16 3h2a2 2 0 012 2v2M20 17v2a2 2 0 01-2 2h-2M8 21H6a2 2 0 01-2-2v-2M4 12h.01M8 12h.01M12 12h.01M16 12h.01M20 12h.01" />
          </svg>
          Scan label
        </WbButton>
      </StickyActionBar>

      {scannerOpen && (
        <BarcodeScanner onDetected={handleScanDetected} onClose={() => setScannerOpen(false)} />
      )}
    </div>
  );
}
